import { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { AsyncView, Button, Card, Chip, Dialog, Notice, Table, useToast } from '../ui';
import { Kebab } from '../ui/Kebab';
import { useResource } from '../lib/useResource';
import { podeAtuar, useSessao } from '../lib/session';
import { api, ApiError } from '../lib/api';
import { FormularioAutomacao, ROTULO_ACAO, ROTULO_CASAMENTO, type Regra, type TipoAcao } from './FormularioAutomacao';

interface Execucao {
  id: string; status: string; attempts: number; startedAt: string;
  autoReply: { name: string } | null;
  conversation: { contact: { profileName: string | null; waId: string } } | null;
}

/* Os rótulos de tipo de casamento e de ação vivem em `FormularioAutomacao`,
   ao lado do formulário que os produz. Esta tela tinha cópias próprias e as
   duas listas estavam ERRADAS: `CASAMENTO` incluía REGEX, que `MatchType` não
   tem, e `ACAO` listava SEND_TEMPLATE, REMOVE_TAG, ASSIGN_AGENT, RESOLVE e
   PAUSE_AUTOMATION — cinco tipos inexistentes — enquanto omitia SEND_BUTTONS,
   AI_REPLY e SEND_FILE, que existem. Como a tela só lia, o defeito aparecia
   apenas como enum cru na coluna "Faz", que é o tipo de coisa que ninguém
   reporta. Mesma família do papel `MANAGER`: rótulo inventado que o banco não
   conhece.
*/

const EXEC: Record<string, { rotulo: string; tom: 'success' | 'danger' | 'warning' | 'neutral' }> = {
  COMPLETED: { rotulo: 'Concluída', tom: 'success' },
  FAILED: { rotulo: 'Falhou', tom: 'danger' },
  RUNNING: { rotulo: 'Em execução', tom: 'warning' },
  SKIPPED: { rotulo: 'Ignorada', tom: 'neutral' },
};

const DIAS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

/**
 * Regras de resposta automática.
 *
 * A tela anterior mostrava só nome e um interruptor. O que a regra realmente
 * faz — quais palavras disparam, sob que condição, e em que ordem as ações
 * acontecem — só aparecia depois de abrir o editor, uma regra por vez. Numa
 * lista de dez regras, era impossível responder "por que essa mensagem recebeu
 * essa resposta?" sem abrir dez editores.
 *
 * Aqui gatilho, condição e cadeia de ações ficam visíveis na própria linha, na
 * ordem em que acontecem. A prioridade também: duas regras que casam com a
 * mesma palavra são resolvidas por ela, e esconder isso é esconder a causa.
 */
export function RespostasAutomaticas() {
  const { agent } = useSessao();
  const toast = useToast();
  const podeEditar = podeAtuar(agent.role, 'ADMIN');

  const regras = useResource<Regra[]>('/workspace/autoreplies');
  // `take: 100` no servidor, sem paginação. Ver `useResource.teto`.
  const execucoes = useResource<Execucao[]>('/workspace/autoreplies/executions/recent', { teto: 100 });
  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState<Regra | null>(null);
  const [excluindo, setExcluindo] = useState<Regra | null>(null);
  const [apagando, setApagando] = useState(false);

  async function excluir() {
    if (!excluindo) return;
    setApagando(true);
    try {
      await api(`/workspace/autoreplies/${excluindo.id}`, { method: 'DELETE' });
      toast(`"${excluindo.name}" foi removida.`);
      setExcluindo(null);
      regras.recarregar();
    } catch (erro) {
      toast(erro instanceof ApiError ? erro.message : 'Falha ao excluir.', 'danger');
    } finally {
      setApagando(false);
    }
  }

  const botaoNova = podeEditar ? (
    <Button variant="primary" icon={<Plus size={18} aria-hidden="true" />} onClick={() => setCriando(true)}>
      Nova automação
    </Button>
  ) : undefined;

  async function alternar(regra: Regra) {
    try {
      await api(`/workspace/autoreplies/${regra.id}`, { method: 'PATCH', body: { enabled: !regra.enabled } });
      regras.recarregar();
      toast(regra.enabled ? `"${regra.name}" desligada.` : `"${regra.name}" ligada.`);
    } catch (erro) {
      toast(erro instanceof ApiError ? erro.message : 'Não foi possível alterar a regra.', 'danger');
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-6)' }}>
      {/* O card ganha título e a explicação da ordem: "100" na coluna Ordem
          não diz nada sozinho, e é justamente o número que decide qual regra
          vence quando duas casam com a mesma mensagem. */}
      <Card
        title="Regras"
        subtitle="Avaliadas de cima para baixo — o menor número roda primeiro, e a primeira que casar interrompe as seguintes."
        as="h2"
        actions={regras.state.status === 'ready' ? botaoNova : undefined}
      >
        <AsyncView
          state={regras.state}
          label="as regras"
          empty={{
            title: 'Nenhuma regra automática',
            text: 'Regras respondem sozinhas a mensagens que casam com palavras-chave, antes de qualquer operador ver a conversa.',
            action: botaoNova,
          }}
        >
          {(linhas) => (
            <Table
              caption="Regras de resposta automática"
              rowKey={(r) => r.id}
              rows={linhas}
              columns={[
                /* Prioridade primeiro: é ela que decide qual regra vence
                   quando duas casam com a mesma mensagem. */
                { key: 'ordem', header: 'Ordem', cell: (r) => String(r.priority) },
                { key: 'nome', header: 'Regra', cell: (r) => r.name },
                { key: 'gatilho', header: 'Dispara quando', cell: (r) => (
                  <span style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>
                      {ROTULO_CASAMENTO[r.matchType] ?? r.matchType}
                    </span>
                    {r.keywords.slice(0, 3).map((k) => <Chip key={k} kind="tag">{k}</Chip>)}
                    {r.keywords.length > 3 && (
                      <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>
                        +{r.keywords.length - 3}
                      </span>
                    )}
                  </span>
                ) },
                { key: 'quando', header: 'Só em', cell: (r) => {
                  const s = r.conditions?.schedule;
                  if (!s) return 'Qualquer horário';
                  return `${s.days.map((d) => DIAS[d]).join(', ')} · ${s.start}–${s.end}`;
                } },
                /* A cadeia numerada mostra a ORDEM. Uma lista sem número deixa
                   parecer que as ações são simultâneas — e "aplicar etiqueta"
                   depois de "resolver conversa" não tem o mesmo efeito que
                   antes. */
                { key: 'acoes', header: 'Faz', cell: (r) => (
                  <ol style={{ margin: 0, paddingInlineStart: 'var(--space-4)', fontSize: 'var(--text-xs)' }}>
                    {r.actions.map((a, i) => (
                      <li key={i}>{ROTULO_ACAO[a.type as TipoAcao] ?? a.type}</li>
                    ))}
                  </ol>
                ) },
                { key: 'estado', header: 'Estado', cell: (r) => (
                  podeEditar ? (
                    <Button
                      variant={r.enabled ? 'secondary' : 'tertiary'}
                      size="sm"
                      onClick={() => void alternar(r)}
                      aria-label={r.enabled ? `Desligar a regra ${r.name}` : `Ligar a regra ${r.name}`}
                    >
                      {r.enabled ? 'Ligada' : 'Desligada'}
                    </Button>
                  ) : (
                    <Chip tone={r.enabled ? 'success' : 'neutral'}>{r.enabled ? 'Ligada' : 'Desligada'}</Chip>
                  )
                ) },
                ...(podeEditar ? [{
                  key: 'acoes', header: 'Ações', align: 'end' as const,
                  cell: (r: Regra) => (
                    <Kebab
                      rotulo={r.name}
                      acoes={[
                        { rotulo: 'Editar', icone: <Pencil size={16} aria-hidden="true" />, onSelect: () => setEditando(r) },
                        { rotulo: 'Excluir', icone: <Trash2 size={16} aria-hidden="true" />, perigo: true, separar: true, onSelect: () => setExcluindo(r) },
                      ]}
                    />
                  ),
                }] : []),
              ]}
            />
          )}
        </AsyncView>
      </Card>

      <Card title="Últimas execuções" subtitle="O que as regras fizeram recentemente, e o que falhou." as="h2">
        <AsyncView
          state={execucoes.state}
          label="as execuções"
          skeletonRows={3}
          empty={{
            title: 'Nenhuma execução ainda',
            text: 'Assim que uma regra disparar, o resultado aparece aqui — inclusive as falhas.',
          }}
        >
          {(linhas) => (
            <Table
              caption="Execuções recentes das regras"
              rowKey={(e) => e.id}
              rows={linhas}
              columns={[
                { key: 'regra', header: 'Regra', cell: (e) => e.autoReply?.name ?? 'Regra removida' },
                { key: 'contato', header: 'Contato', cell: (e) => e.conversation?.contact.profileName ?? e.conversation?.contact.waId ?? '—' },
                { key: 'quando', header: 'Quando', cell: (e) => new Date(e.startedAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) },
                { key: 'tentativas', header: 'Tentativas', cell: (e) => String(e.attempts) },
                { key: 'status', header: 'Resultado', cell: (e) => {
                  const s = EXEC[e.status] ?? { rotulo: e.status, tom: 'neutral' as const };
                  return <Chip tone={s.tom}>{s.rotulo}</Chip>;
                } },
              ]}
            />
          )}
        </AsyncView>
      </Card>

      {!podeEditar && (
        <Notice tone="info">
          Você pode acompanhar as regras, mas criar e editar é restrito a administradores.
        </Notice>
      )}

      {/* Montado só quando aberto: o editor carrega etiquetas, arquivos,
          integrações e números ao montar, e manter isso vivo o tempo todo
          faria a tela pagar quatro requisições que quase nunca serão usadas. */}
      {criando && (
        <FormularioAutomacao
          onFechar={() => setCriando(false)}
          onSalvo={(mensagem) => { setCriando(false); regras.recarregar(); toast(mensagem); }}
        />
      )}
      {editando && (
        <FormularioAutomacao
          key={editando.id}
          regra={editando}
          onFechar={() => setEditando(null)}
          onSalvo={(mensagem) => { setEditando(null); regras.recarregar(); toast(mensagem); }}
        />
      )}

      <Dialog
        open={excluindo !== null}
        onClose={() => setExcluindo(null)}
        title="Confirmar exclusão"
        footer={
          <>
            <Button variant="secondary" onClick={() => setExcluindo(null)}>Cancelar</Button>
            <Button variant="danger" loading={apagando} icon={<Trash2 size={18} aria-hidden="true" />} onClick={() => void excluir()}>
              Excluir definitivamente
            </Button>
          </>
        }
      >
        <p>
          {excluindo
            ? `A regra "${excluindo.name}" será apagada. Mensagens que casavam com ela passam a esperar atendimento humano.`
            : ''}
        </p>
      </Dialog>
    </div>
  );
}
